import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ChatActionsMenu.tsx -- per-chat-window actions menu.
//
// Anchored to a "+" button in the AssistantWindow header. Styled to
// match the desktop's right-click context menu (.context-menu /
// .context-menu-item) so the look is consistent across the app and
// reads as a familiar native-style menu.
//
// Positioning: opens BELOW + to the RIGHT of the trigger button.
// "Right of the cursor" is the standard convention for context menus
// (Windows / macOS / Linux file managers all do this). The previous
// implementation right-aligned to the trigger, which made the menu
// drop to the LEFT of where the user clicked -- felt unnatural.
//
// Items: no subtitles, no emojis. Single-line labels with optional
// shortcut hint on the right (matches the desktop ContextMenu look).
//
// Distinct from the desktop's PlusMenu: that one is project-wide
// (create folder, upload, agents panel...). This one is window-scoped.
import { useEffect, useRef } from 'react';
export function ChatActionsMenu({ open, anchorRect, onClose, items }) {
    const popRef = useRef(null);
    // Close on outside click + Escape.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (popRef.current && !popRef.current.contains(e.target))
                onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);
    if (!open || !anchorRect)
        return null;
    // Position: drop below the trigger. Default: left edge of menu
    // aligns with left edge of trigger -- this puts the menu BODY to
    // the right of the cursor that just clicked the trigger (the
    // standard "context menu opens right + down" convention).
    //
    // The menu is content-sized (CSS rules don't fix a width), so we
    // measure it after first render to clamp -- but for the first
    // render we use a sensible 200px estimate to keep the initial
    // position correct. The measure pass on mount fixes any overflow.
    const ESTIMATED_W = 200;
    const left = Math.min(Math.max(8, anchorRect.x), window.innerWidth - ESTIMATED_W - 8);
    const top = Math.min(anchorRect.y + anchorRect.height + 4, window.innerHeight - 80);
    const visible = items.filter((it) => !it.hidden);
    if (visible.length === 0)
        return null;
    return (_jsx("div", { ref: popRef, className: "context-menu chat-actions-menu", style: {
            // Override the .context-menu z-index (200) so the menu sits
            // above floating chat windows (1000..1000999) and the FAB
            // (1001000). Modals at 2000000 still beat us.
            left,
            top,
            zIndex: 1001500,
        }, role: "menu", "aria-label": "Chat actions", children: visible.map((it, i) => (_jsxs("button", { type: "button", className: 'context-menu-item'
                + (it.destructive ? ' destructive' : ''), onClick: () => {
                if (it.disabled)
                    return;
                it.onClick();
                onClose();
            }, disabled: it.disabled ?? false, role: "menuitem", children: [_jsx("span", { children: it.label }), it.shortcut && _jsx("span", { className: "shortcut", children: it.shortcut })] }, i))) }));
}
